<template>
  <div
    class="spotlight"
    :class="{
      'spotlight--expanded': expanded,
      'spotlight--disabled': !enabled
    }"
    @click.capture="onClick">
    <div
      class="spotlight__spotlight"
      :style="{ background }">
    </div>
    <div class="spotlight__content black--text">
      <div class="spotlight__content__inner">
        <slot />
      </div>
    </div>
  </div>
</template>
<script>
export default {
  props: {
    enabled: {
      type: Boolean,
      default: true
    },
    maxFade: {
      type: Number,
      default: 100
    }
  },
  data () {
    const spotlightWidth = 75
    return {
      expanded: false,
      tickingScroll: false,
      blur: 0,
      maxBlur: this.maxFade / 100,
      minBlur: 0,
      minWindow: 0,
      maxWindow: 0,
      clientX: Math.round(Math.random() * (window.innerWidth - spotlightWidth)),
      clientY: Math.round(Math.random() * (window.innerHeight - spotlightWidth)),
      style: {},
      spotlightWidth,
      animationFrame: null
    }
  },
  computed: {
    background () {
      if (!this.enabled) {
        return `rgba(0, 0, 0, ${this.blur}`
      }

      if (this.expanded) {
        return `radial-gradient(75px 75px at ${this.clientX}px ${this.clientY}px, rgba(0, 0, 0, ${this.blur}) 0, rgba(0, 0, 0, ${this.blur}) ${this.spotlightWidth}px, rgba(0, 0, 0, 1) 85px)`
      }

      return `radial-gradient(75px 75px at ${this.clientX}px ${this.clientY}px, rgba(0, 0, 0, ${this.blur}) 0, rgba(0, 0, 0, ${this.blur}) ${this.spotlightWidth}px, rgba(0, 0, 0, 1) 95px)`
    }
  },
  methods: {
    // Input:
    // Center of the circle (x, y)
    // Radius of circle: r
    // Point inside a circle (a, b)
    pointWithinCircle (a, b, x, y, r) {
      const distPoints = (a - x) * (a - x) + (b - y) * (b - y)
      r *= r
      if (distPoints < r) {
        return true
      }
      return false
    },
    onMouseMove (event) {
      if (this.expanded) {
        return
      }

      window.requestAnimationFrame(() => {
        this.clientX = event.clientX
        this.clientY = event.clientY
      })
    },
    onTouchMove (event) {
      if (this.expanded) {
        return
      }

      const touch = event.changedTouches[0]
      if (!touch) {
        return
      }

      // if we don't have our finger within the circle, don't move the spotlight
      if (!this.pointWithinCircle(touch.clientX, touch.clientY, this.clientX, this.clientY, this.spotlightWidth)) {
        return
      }

      event.preventDefault()
      event.stopImmediatePropagation()

      window.requestAnimationFrame(() => {
        this.clientX = touch.clientX
        this.clientY = touch.clientY
      })
    },
    mapRange (value, minIn, maxIn, minOut, maxOut) {
      return (value - minIn) * (maxOut - minOut) / (maxIn - minIn) + minOut
    },
    onRequestScroll (event) {
      if (this.tickingScroll) {
        return
      }

      this.tickingScroll = true
      window.requestAnimationFrame(() => {
        this.onScroll(event)
      })
    },
    onScroll (event) {
      const top = (window.pageYOffset || document.documentElement.scrollTop) - (document.documentElement.clientTop || 0)
      let blur = this.mapRange(top, this.minWindow, this.maxWindow, this.minBlur, this.maxBlur)
      if (blur > this.maxBlur) {
        blur = this.maxBlur
      }

      this.blur = blur

      this.tickingScroll = false
    },
    onResize () {
      const height = window.innerHeight ||
         document.documentElement.clientHeight ||
         document.body.clientHeight
      this.maxWindow = height
    },
    onClick () {
      this.expanded = !this.expanded
      this.transitionSpotlight()
    },
    animate (callback) {
      window.cancelAnimationFrame(this.animationFrame)
      return new Promise((resolve) => {
        this.animationFrame = window.requestAnimationFrame(() => {
          callback()
          resolve()
        })
      })
    },
    async transitionSpotlight () {
      const longestSide = window.innerHeight < window.innerWidth
        ? window.innerWidth
        : window.innerHeight
      const goalWidth = this.expanded ? 2 * longestSide : 75
      const widthIncrement = this.expanded ? 80 : -80
      const widthComparison = this.expanded
        ? () => this.spotlightWidth <= goalWidth
        : () => this.spotlightWidth >= goalWidth

      while (widthComparison()) {
        await this.animate(() => {
          this.spotlightWidth += widthIncrement
        })
      }

      this.spotlightWidth = goalWidth
    }
  },
  created () {
    // TODO: the blur should not be part of the spotlight
    window.addEventListener('scroll', this.onRequestScroll)
    window.addEventListener('resize', this.onResize)

    if (!this.enabled) {
      return
    }

    document.addEventListener('mousemove', this.onMouseMove)
    document.addEventListener('touchmove', this.onTouchMove, { capture: true, passive: false })
  },
  mounted () {
    this.onResize()
  },
  destroyed () {
    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('touchmove', this.onTouchMove, { capture: true, passive: false })
    window.removeEventListener('scroll', this.onRequestScroll)
    window.removeEventListener('resize', this.onResize)
  }
}
</script>
<style lang="stylus">
@require '~vuetify/src/stylus/settings/_variables.styl'
@require '../assets/styles/base'

.spotlight
  position: fixed;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  cursor: none;

  &.spotlight--expanded,
  &.spotlight--disabled
    cursor: auto;

  @media print
    display none

.spotlight__spotlight {
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 2;
  background: radial-gradient(75px 75px at 49% 49%, transparent 0px, transparent 75px, rgb(0, 0, 0) 95px);
  pointer-events: none;
}

.spotlight__content {
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 1;
}
</style>
